import { can, type RunDetail, type StageDetail } from '@atlas-control/shared';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { Pause, Play, RefreshCw, XCircle } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { DisabledReason } from '@/components/disabled-reason';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { runQuery, stagesQuery, useRunMutations } from '@/features/runs/api';
import { RunStatusBadge, StageTimeline } from '@/features/runs/components';
import { stageLabel } from '@/features/runs/labels';
import { useMe } from '@/lib/auth';
import { formatDateTime, formatRelative } from '@/lib/format';
import { guard } from '@/lib/guards';
import { toastError, toastSuccess } from '@/lib/notify';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/runs/$runId')({
  beforeLoad: ({ context }) => guard(context.me, 'runs.read'),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(runQuery(params.runId)),
  component: RunLayout,
});

/** Tabs are child routes; modules add theirs here as they land. */
const TABS = [
  { to: '/runs/$runId', label: 'Overview', exact: true },
  { to: '/runs/$runId/final-review', label: 'Final review', exact: false },
  { to: '/runs/$runId/email', label: 'Email', exact: false },
  { to: '/runs/$runId/downloads', label: 'Downloads', exact: false },
  { to: '/runs/$runId/activity', label: 'Activity', exact: false },
] as const;

function RunLayout() {
  const { runId } = Route.useParams();
  const { data: run, refetch, isFetching } = useSuspenseQuery(runQuery(runId));
  const stages = useQuery(stagesQuery(runId));
  const { user } = useMe();
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const isDev = can(user.role, 'stages.details');

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{run.company}</h1>
            <RunStatusBadge status={run.status} />
          </div>
          <p className="mt-1 flex flex-wrap gap-x-3 text-sm text-muted-foreground">
            <Link
              to="/forms/$formId"
              params={{ formId: run.formId }}
              className="underline-offset-4 hover:underline"
            >
              Form
            </Link>
            {run.contactEmail && <span>{run.contactEmail}</span>}
            <span>
              Started {formatDateTime(run.startedAt)}
              {run.startedBy ? ` by ${run.startedBy.name}` : ''}
            </span>
            {run.atlasRunId && (
              <span className="font-mono text-xs leading-5">Atlas {run.atlasRunId}</span>
            )}
          </p>
        </div>
        {can(user.role, 'runs.control') && <RunControls run={run} />}
      </div>

      {run.statusReason && run.status === 'failed' && (
        <p className="mb-4 rounded-md border border-danger/40 bg-danger-soft p-3 text-sm text-danger">
          {run.statusReason}
        </p>
      )}

      <Card className="mb-4">
        <CardContent className="space-y-2">
          {stages.data ? (
            <StageTimeline stages={stages.data} onSelect={isDev ? setSelectedStage : undefined} />
          ) : (
            <p className="text-sm text-muted-foreground">
              {stages.isError
                ? 'Stage progress is unavailable (Atlas did not answer).'
                : 'Loading stages…'}
            </p>
          )}
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            Last synced with Atlas {formatRelative(run.stageSyncedAt)}
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label="Refresh"
              disabled={isFetching}
              onClick={() => void refetch().then(() => stages.refetch())}
            >
              <RefreshCw className={cn('size-3.5', isFetching && 'animate-spin')} />
            </Button>
          </p>
        </CardContent>
      </Card>

      <nav className="mb-6 flex gap-1 overflow-x-auto border-b" aria-label="Run sections">
        {TABS.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            params={{ runId }}
            activeOptions={{ exact: t.exact }}
            className="-mb-px border-b-2 border-transparent px-3 py-2 text-sm whitespace-nowrap text-muted-foreground hover:text-foreground data-[status=active]:border-primary data-[status=active]:font-medium data-[status=active]:text-foreground"
          >
            {t.label}
          </Link>
        ))}
      </nav>
      <Outlet />

      <StageSheet
        name={selectedStage}
        rows={(stages.data ?? []).filter(
          (s): s is StageDetail => 'attempts' in s && s.name === selectedStage,
        )}
        onClose={() => setSelectedStage(null)}
      />
    </>
  );
}

function RunControls({ run }: { run: RunDetail }) {
  const { pause, resume, cancel } = useRunMutations(run.id);
  const [confirm, setConfirm] = useState<'pause' | 'resume' | 'cancel' | null>(null);
  const ended = ['completed', 'cancelled', 'failed'].includes(run.status);

  const pauseReason = !run.capabilities.pause
    ? 'Atlas does not support pause yet (pending P3)'
    : run.status !== 'running'
      ? 'Only a running run can be paused'
      : null;
  const resumeReason = !run.capabilities.resume
    ? 'Atlas does not support resume yet (pending P3)'
    : run.status !== 'paused' && run.status !== 'pausing'
      ? 'Only a paused run can be resumed'
      : null;

  const act = (m: typeof pause, msg: string) =>
    m.mutate(undefined, {
      onSuccess: () => {
        toastSuccess(msg);
        setConfirm(null);
      },
      onError: (err) => toastError(err),
    });

  if (ended) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <DisabledReason reason={pauseReason}>
        <Button
          variant="outline"
          disabled={Boolean(pauseReason)}
          onClick={() => setConfirm('pause')}
        >
          <Pause /> Pause
        </Button>
      </DisabledReason>
      <DisabledReason reason={resumeReason}>
        <Button
          variant="outline"
          disabled={Boolean(resumeReason)}
          onClick={() => setConfirm('resume')}
        >
          <Play /> Resume
        </Button>
      </DisabledReason>
      <Button variant="outline" className="text-danger" onClick={() => setConfirm('cancel')}>
        <XCircle /> Cancel run
      </Button>

      <ConfirmDialog
        open={confirm === 'pause'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Pause this run?"
        description="The stage that is running now finishes and its output is kept; then the run halts until someone resumes it."
        confirmLabel="Pause"
        pending={pause.isPending}
        onConfirm={() => act(pause, 'Pause requested')}
      />
      <ConfirmDialog
        open={confirm === 'resume'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Resume this run?"
        description="Atlas continues from the next stage."
        confirmLabel="Resume"
        pending={resume.isPending}
        onConfirm={() => act(resume, 'Run resumed')}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Cancel this run permanently?"
        description={
          <>
            <p>The run ends and can never be resumed. No review or email will follow.</p>
            {!run.capabilities.cancelInAtlas && (
              <p className="font-medium text-warning">
                Atlas cannot be told to stop yet (pending P3): stages already running there will
                finish, but the panel will not move the run on.
              </p>
            )}
          </>
        }
        confirmLabel="Cancel run"
        destructive
        typeToConfirm={run.company}
        pending={cancel.isPending}
        onConfirm={() => act(cancel, 'Run cancelled')}
      />
    </div>
  );
}

function StageSheet({
  name,
  rows,
  onClose,
}: {
  name: string | null;
  rows: StageDetail[];
  onClose: () => void;
}) {
  return (
    <Sheet open={name !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        {name && (
          <>
            <SheetHeader>
              <SheetTitle>{stageLabel(name)}</SheetTitle>
              <SheetDescription>
                Developer detail from Atlas{rows.length > 1 ? `, ${rows.length} departments` : ''}.
              </SheetDescription>
            </SheetHeader>
            <div className="space-y-4 px-4 pb-6">
              {rows.map((r) => (
                <div key={r.departmentId ?? 'stage'} className="rounded-lg border p-3 text-sm">
                  <dl className="grid grid-cols-2 gap-y-1">
                    {r.departmentId && (
                      <>
                        <dt className="text-muted-foreground">Department</dt>
                        <dd className="font-mono text-xs leading-5">{r.departmentId}</dd>
                      </>
                    )}
                    <dt className="text-muted-foreground">Status</dt>
                    <dd>{r.status}</dd>
                    <dt className="text-muted-foreground">Attempts</dt>
                    <dd>{r.attempts}</dd>
                    <dt className="text-muted-foreground">Tokens in / out</dt>
                    <dd className="tabular-nums">
                      {r.tokensIn.toLocaleString()} / {r.tokensOut.toLocaleString()}
                    </dd>
                    <dt className="text-muted-foreground">Cost</dt>
                    <dd className="tabular-nums">
                      {r.costUsd === null ? '-' : `$${r.costUsd.toFixed(4)}`}
                    </dd>
                    <dt className="text-muted-foreground">Duration</dt>
                    <dd className="tabular-nums">{(r.durationMs / 1000).toFixed(1)} s</dd>
                    <dt className="text-muted-foreground">Model</dt>
                    <dd>{r.modelUsed ?? '-'}</dd>
                  </dl>
                  {r.errors.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {r.errors.map((e, i) => (
                        <li key={i} className="rounded bg-danger-soft p-2 text-xs text-danger">
                          <span className="font-medium">{e.type}</span> {e.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
